import { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';
import { roundUp } from '../../../../utils/roundUp';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { MarketDataMapControls } from './MarketDataMapControls';
import {
  getAllVacancyRateMapping,
  useCompetitorsWithSpecialOffer,
  useNewInventory,
  useTopNewConstructions,
} from './MarketDataMap.utils';

import { useEffect, useState } from 'react';
import { Toast, useToast } from '@zillow/constellation-10';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  type PortfolioAnalyticsPropertyResponseDto,
  TopConstructionStatus,
} from '@zg-rentals/ts-customer-analytics-client';
import { isClaimedProperty } from '../../../../utils/isClaimedProperty';
import { MapDot, type MapDotPayload } from '../../../SharedComponents/Map/Dots/Dots';
import { CustomerAnalyticsPage } from '../../../SharedComponents/CustomerAnalyticsPage';
import { MarketDataMapSidebar } from './MarketDataMapSidebar';
import { useSidebarTitle } from '../../../../hooks/useSidebarTitle';
import dynamic from 'next/dynamic';
import { PageLoading } from '../../../SharedComponents/PageLoading';

const MapWithGeoJson = dynamic(
  () => import('../../../SharedComponents/Map/MapWithGeoJson').then((mod) => mod.MapWithGeoJson),
  {
    ssr: false,
    loading: () => <PageLoading />,
  },
);

const getLatLonKey = (lat: number, lon: number) => `${lat}-${lon}`;

export interface Filters {
  showAdvertisedProperties: boolean;
  showClaimedProperties: boolean;
  showSpecialOffers: boolean;
  showNewInventoryOnZillow: boolean;
  showNowLeasing: boolean;
  showUnderConstruction: boolean;
  showPlannedDevelopment: boolean;
}

export const regionsWithMarkers = [
  DisplayingRegionLevel.METRO_AREA,
  DisplayingRegionLevel.CITY,
  DisplayingRegionLevel.ZIP,
];

export const useMarketDataMapData = () => {
  const { allProperties, isFetchingInitialData } = useMainLayout();
  const [isCalculatingVacancyRate, setIsCalculatingVacancyRate] = useState(false);
  const [vacancyRateMapping, setVacancyRateMapping] = useState<Map<number, number> | null>(null);

  useEffect(() => {
    if (!isFetchingInitialData && allProperties?.length > 0 && !vacancyRateMapping) {
      setIsCalculatingVacancyRate(true);
      const allVacancyRateMapping = getAllVacancyRateMapping(allProperties);
      setVacancyRateMapping(allVacancyRateMapping);
      setIsCalculatingVacancyRate(false);
    }
  }, [allProperties, isFetchingInitialData, vacancyRateMapping]);

  const renderDotNumber = (regionId: number, _: number) => {
    const vacancyRate = vacancyRateMapping?.get(regionId) ?? 0;
    if (vacancyRate > 1) {
      return 'No data';
    }
    return `${roundUp(vacancyRate * 100, 1)}%`;
  };

  return {
    renderDotNumber,
    isCalculatingVacancyRate,
  };
};

export const MarketDataMap = () => {
  const { filteredProperties, selectedRegionId, selectedDisplayingRegionLevel, visibleRegionIds, displayMode } =
    useMainLayout();
  const [filters, setFilters] = useState<Filters>({
    showAdvertisedProperties: true,
    showClaimedProperties: true,
    showSpecialOffers: true,
    showNewInventoryOnZillow: true,
    showNowLeasing: true,
    showUnderConstruction: true,
    showPlannedDevelopment: true,
  });
  const { enqueueToast } = useToast();
  const sidebarTitle = useSidebarTitle();
  const { renderDotNumber, isCalculatingVacancyRate } = useMarketDataMapData();

  const finalDisplayMode = selectedRegionId ? selectedDisplayingRegionLevel : displayMode;
  const visibleProperties: Array<PortfolioAnalyticsPropertyResponseDto> = [];

  if (selectedRegionId && selectedDisplayingRegionLevel !== DisplayingRegionLevel.STATE) {
    for (const property of filteredProperties) {
      if (
        property.propertyDetails.cityRegionId === Number(selectedRegionId) ||
        property.propertyDetails.metroStatRegionId === Number(selectedRegionId) ||
        property.propertyDetails.zipRegionId === Number(selectedRegionId)
      ) {
        visibleProperties.push(property);
      }
    }
  } else if (regionsWithMarkers.includes(finalDisplayMode)) {
    visibleRegionIds.forEach(({ regionId }) => {
      for (const property of filteredProperties) {
        if (
          property.propertyDetails.cityRegionId === Number(regionId) ||
          property.propertyDetails.metroStatRegionId === Number(regionId) ||
          property.propertyDetails.zipRegionId === Number(regionId)
        ) {
          visibleProperties.push(property);
        }
      }
    });
  }

  const {
    data: competitorsWithSpecialOffer,
    isLoading: isLoadingCompetitorsWithSpecialOffer,
    error: errorCompetitorsWithSpecialOffer,
  } = useCompetitorsWithSpecialOffer(regionsWithMarkers.includes(finalDisplayMode) && filters.showSpecialOffers);

  const {
    data: newInventory,
    isLoading: isLoadingNewInventory,
    error: errorNewInventory,
  } = useNewInventory(regionsWithMarkers.includes(finalDisplayMode) && filters.showNewInventoryOnZillow);

  const {
    data: topNewConstructions,
    isLoading: isLoadingTopNewConstructions,
    error: errorTopNewConstructions,
  } = useTopNewConstructions(
    regionsWithMarkers.includes(finalDisplayMode) &&
      (filters.showUnderConstruction || filters.showPlannedDevelopment || filters.showNowLeasing),
  );

  useEffect(() => {
    if (errorCompetitorsWithSpecialOffer || errorNewInventory || errorTopNewConstructions) {
      const error = errorCompetitorsWithSpecialOffer || errorNewInventory || errorTopNewConstructions;
      const errorMessage = DatarouterError.getDisplayMessage(error, 'Error fetching data');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  }, [errorCompetitorsWithSpecialOffer, errorNewInventory, errorTopNewConstructions, enqueueToast]);

  const onFilterChange = (filter: keyof Filters, value: boolean) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filter]: value,
    }));
  };

  const advertisedProperties = visibleProperties.filter((property) => !isClaimedProperty(property));
  const claimedProperties = visibleProperties.filter((property) => isClaimedProperty(property));

  const nowLeasing =
    topNewConstructions?.filter((construction) => construction.constructionStatus === TopConstructionStatus.COMPLETE) ??
    [];
  const underConstruction =
    topNewConstructions?.filter(
      (construction) => construction.constructionStatus === TopConstructionStatus.UNDER_CONSTRUCTION,
    ) ?? [];
  const plannedDevelopment =
    topNewConstructions?.filter(
      (construction) => construction.constructionStatus === TopConstructionStatus.PLANNED_DEVELOPMENT,
    ) ?? [];

  const competitorsToRender = filters.showSpecialOffers ? (competitorsWithSpecialOffer ?? []) : [];
  const newInventoryToRender = filters.showNewInventoryOnZillow ? (newInventory ?? []) : [];
  let topNewConstructionsToRender = topNewConstructions ?? [];
  let propertiesToRender: Array<PortfolioAnalyticsPropertyResponseDto & { visible?: boolean }> = visibleProperties;

  if (!filters.showNowLeasing) {
    topNewConstructionsToRender = topNewConstructionsToRender.filter(
      (topNewConstruction) => topNewConstruction.constructionStatus !== TopConstructionStatus.COMPLETE,
    );
  }
  if (!filters.showUnderConstruction) {
    topNewConstructionsToRender = topNewConstructionsToRender.filter(
      (topNewConstruction) => topNewConstruction.constructionStatus !== TopConstructionStatus.UNDER_CONSTRUCTION,
    );
  }
  if (!filters.showPlannedDevelopment) {
    topNewConstructionsToRender = topNewConstructionsToRender.filter(
      (topNewConstruction) => topNewConstruction.constructionStatus !== TopConstructionStatus.PLANNED_DEVELOPMENT,
    );
  }

  if (!filters.showClaimedProperties || !filters.showAdvertisedProperties) {
    propertiesToRender = propertiesToRender.map((property) => {
      const isClaimed = isClaimedProperty(property);

      if (!filters.showClaimedProperties && isClaimed) {
        return { ...property, visible: false };
      }
      if (!filters.showAdvertisedProperties && !isClaimed) {
        return { ...property, visible: false };
      }

      return { ...property, visible: true };
    });
  }

  const allDotsMap = new Map<
    string,
    {
      key: string;
      payload: MapDotPayload;
    }
  >();

  propertiesToRender.forEach((property) => {
    const propertyKey = getLatLonKey(property.propertyDetails.lat, property.propertyDetails.lon);

    const key = `${propertyKey}-property`;
    if (!allDotsMap.has(key)) {
      allDotsMap.set(key, {
        key,
        payload: isClaimedProperty(property)
          ? ({
              type: 'claimedProperty',
              data: property,
              visible: property.visible,
              zIndex: 1000000,
            } as const)
          : ({ type: 'property', data: property, visible: property.visible, zIndex: 1000000 } as const),
      });
    }
  });

  competitorsToRender.forEach((competitor) => {
    const competitorKey = getLatLonKey(competitor.lat, competitor.lon);

    const key = `${competitorKey}-specialOffer`;

    if (!allDotsMap.has(key)) {
      allDotsMap.set(key, {
        key,
        payload: { type: 'specialOffers', data: competitor },
      });
    }
  });

  newInventoryToRender.forEach((inv) => {
    const newInventoryKey = getLatLonKey(
      inv.data.propertyInfo.geoRegion.latitude,
      inv.data.propertyInfo.geoRegion.longitude,
    );

    const key = `${newInventoryKey}-newInventory`;

    if (!allDotsMap.has(key)) {
      allDotsMap.set(key, {
        key,
        payload: { type: 'newInventory', data: inv },
      });
    }
  });

  topNewConstructionsToRender.forEach((construction) => {
    const constructionKey = getLatLonKey(construction.lat, construction.lon);

    const key = `${constructionKey}-newConstruction`;

    if (!allDotsMap.has(key)) {
      allDotsMap.set(key, {
        key,
        payload: { type: 'newConstruction', data: construction },
      });
    }
  });

  const allDots = allDotsMap.values().toArray();

  return (
    <CustomerAnalyticsPage
      header="Market data"
      subheader="Source: Zillow internal data"
      sidebarTitle={sidebarTitle}
      sidebar={<MarketDataMapSidebar />}
    >
      <MapWithGeoJson renderDotNumber={renderDotNumber} loading={isCalculatingVacancyRate}>
        <MarketDataMapControls
          filters={filters}
          onFilterChange={onFilterChange}
          isFetchingSpecialOffers={isLoadingCompetitorsWithSpecialOffer}
          isFetchingNewInventory={isLoadingNewInventory}
          isFetchingTopNewConstructions={isLoadingTopNewConstructions}
          advertisedProperties={advertisedProperties}
          claimedProperties={claimedProperties}
          competitorsWithSpecialOffer={competitorsWithSpecialOffer ?? []}
          newInventory={newInventory ?? []}
          nowLeasing={nowLeasing}
          underConstruction={underConstruction}
          plannedDevelopment={plannedDevelopment}
        />
        {allDots.map(({ key, payload }) => (
          <MapDot key={key} payload={payload} />
        ))}
      </MapWithGeoJson>
    </CustomerAnalyticsPage>
  );
};
